import { HttpClient, HttpParams } from '@angular/common/http';
// Angular modules.
import { Injectable } from '@angular/core';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { dummyUserId } from 'app/constants';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IRecurrencyScheduleResponse } from 'app/interfaces/appointment.interface';
import { IClientEvent } from 'app/interfaces/contact.interface';
// Zenklub interfaces.
import {
	EventPrice,
	ICalendarEvent,
	IConfirmEvent,
	IEvent,
	IEventList,
	INewEvent,
	IRecurrencyAppointment,
	IUpdateEvent,
	IValidateStartResponse,
	SearchEvents,
} from 'app/interfaces/event.interface';
import { IProfessionalBankValidationStatus } from 'app/interfaces/professionalprofile.interface';
import {
	IRecurrenciesAttemptsParams,
	IRecurrenciesAttemptsResponse,
} from 'app/interfaces/recurrent-attempts.interface';
import { environment } from 'environments/environment';
import firebase from 'firebase';
import * as moment from 'moment-timezone';
import { PrimeNGConfig } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

// Zenklub services.
import { ActionTrackerService } from './actiontracker.service';
import { CacheService } from './cache.service';
import { ContactsService } from './contacts.service';
import { CurrencyService } from './currency.service';
import { DateTimeService } from './datetime.service';
import { UserService } from './user.service';

@Injectable()
export class EventsService {
	private newOnlineEvent = new Subject<ICalendarEvent>();
	private updatedEvent = new Subject<ICalendarEvent>();
	private idWallEvent = new Subject<ICalendarEvent>();
	private wePayOutEvent = new Subject<ICalendarEvent>();

	private refreshTimeout: any;
	private limit = 500;
	private skip = 0;

	// Cache service
	collectionName = 'events';

	// Events control.
	eventsList: string[] = [];
	eventsListUpdated: string[] = [];

	// Translated strings.
	strStatusCancelled: string;
	strStatusConfirmed: string;
	strStatusConfirmedNo: string;
	strStatusConfirmedNoShow: string;
	strStatusInvite: string;
	strStatusNew: string;
	strStatusPaid: string;
	strStatusReschedule: string;
	strStatusRescheduled: string;
	strStatusRescheduleRequest: string;
	strStatusWaitingPayment: string;
	subscription: Subscription;

	noStatus = ['new', 'cancelled', 'reschedule'];

	private firebaseUser: firebase.User;

	// Constructor method.
	constructor(
		private actionTracker: ActionTrackerService,
		private dateTime: DateTimeService,
		private http: HttpClient,
		private translate: TranslateService,
		private currency: CurrencyService,
		private contacts: ContactsService,
		private user: UserService,
		private cacheService: CacheService,
		private config: PrimeNGConfig
	) {
		this.user.getFirebaseUser().then((user) => (this.firebaseUser = user));

		if (!translate.currentLang || !translate.defaultLang) {
			translate.setDefaultLang('pt-BR');
			translate.use('pt-BR');
		}

		// Get translated texts.
		this.translate
			.get([
				'SCHEDULE.STATUS.CANCELLED',
				'SCHEDULE.STATUS.INVITE',
				'SCHEDULE.STATUS.NEW',
				'SCHEDULE.STATUS.PAID',
				'SCHEDULE.STATUS.RESCHEDULE',
				'SCHEDULE.STATUS.RESCHEDULED',
				'SCHEDULE.STATUS.RESCHEDULE_REQUEST',
				'SCHEDULE.STATUS.WAITINGPAYMENT',
				'SCHEDULE.STATUS.CONFIRMED_YES',
				'SCHEDULE.STATUS.CONFIRMED_NO',
				'SCHEDULE.STATUS.CONFIRMED_NOSHOW',
			])
			.subscribe((res) => {
				// Strings.
				this.strStatusCancelled = res['SCHEDULE.STATUS.CANCELLED'];
				this.strStatusConfirmed = res['SCHEDULE.STATUS.CONFIRMED_YES'];
				this.strStatusConfirmedNo = res['SCHEDULE.STATUS.CONFIRMED_NO'];
				this.strStatusConfirmedNoShow = res['SCHEDULE.STATUS.CONFIRMED_NOSHOW'];
				this.strStatusInvite = res['SCHEDULE.STATUS.INVITE'];
				this.strStatusNew = res['SCHEDULE.STATUS.NEW'];
				this.strStatusPaid = res['SCHEDULE.STATUS.PAID'];
				this.strStatusReschedule = res['SCHEDULE.STATUS.RESCHEDULE'];
				this.strStatusRescheduled = res['SCHEDULE.STATUS.RESCHEDULED'];
				this.strStatusRescheduleRequest =
					res['SCHEDULE.STATUS.RESCHEDULE_REQUEST'];
				this.strStatusWaitingPayment = res['SCHEDULE.STATUS.WAITINGPAYMENT'];
			});

		this.subscription = this.translate.stream('primeng').subscribe((data) => {
			this.config.setTranslation(data);
		});
	}

	// Get events from user.
	private getEventsFromServer(search: SearchEvents): Observable<IEventList> {
		// did we got all events?
		let params = new HttpParams();
		params = params.set(
			'limit',
			search.limit ? search.limit.toString(10) : this.limit.toString(10)
		);
		params = params.set(
			'skip',
			search.skip ? search.skip.toString(10) : this.skip.toString(10)
		);

		// should we set the cache week variable?
		if (search.setWeek) {
			localStorage.setItem('dbEventsWeek', moment.utc().isoWeek().toString());
		}

		// in case we want to search without date filter
		if (!search.ignoreDates) {
			const ds = search.minDate
				? search.minDate.toISOString()
				: moment.utc().startOf('week').subtract(1, 'week').toISOString();
			if (search.maxDate) {
				params = params.set('maxStartDate', search.maxDate.toISOString());
			} else {
				params = params.set(
					'maxStartDate',
					moment.utc().endOf('week').add(1, 'week').toISOString()
				);
			}
			params = params.set('minStartDate', ds);
		}

		if (search.status) {
			params = params.set('status', search.status.join(','));
		} else {
			params = params.set(
				'status',
				'new,invite,paid,reserved,reschedule,reschedule_request,cancelled'
			);
		}

		if (search.userIds) {
			params = params.set('userIds', search.userIds.join(','));
		}

		if (search.onlyMissingTags) {
			params = params.set('onlyMissingTags', '1');
		}

		return this.http
			.get<IEventList>('appointments', { params })
			.pipe(debounceTime(500));
	}

	// Update local db with events.
	public updateLocalEvents(search: SearchEvents): Promise<void> {
		return new Promise((resolve, reject) => {
			// Get events from server.
			this.getEventsFromServer(search).subscribe(
				(result) => {
					if (!result || !result.items) {
						return resolve();
					}
					const skip = search.skip ? search.skip : 0;
					const total = skip + result.items.length;

					if (result.total > total) {
						this.refreshTimeout = setTimeout(() => {
							clearTimeout(this.refreshTimeout);
							search.skip = total;
							this.updateLocalEvents(search);
						}, 1000);
					} else {
						// set last updated
						localStorage.setItem('zklastevup', moment.utc().toISOString());
						clearTimeout(this.refreshTimeout);
					}

					// Save new events.
					const addPromises = result.items.map((el) => {
						return this.updateLocalEvent(el);
					});

					return Promise.all(addPromises).then(
						() => {
							return resolve();
						},
						(error) => {
							return reject(error);
						}
					);
				},
				(error) => {
					return reject(error);
				}
			);
		});
	}

	// Saves an event in cache
	public updateLocalEvent(event: IEvent): Promise<ICalendarEvent> {
		return this.cacheService.update(
			this.collectionName,
			this.formatEvent(event)
		);
	}

	public async clearEventsCache() {
		await this.cacheService.deleteStore(this.collectionName);
	}

	// Verify new data.
	public async verifyNewEvent(event: IEvent): Promise<void> {
		const nevent = this.formatEvent(event);

		if (event.status === 'REJEITADO' || event.status === 'APROVADO') {
			this.cacheService.update('idwallResponse', event);
			this.idWallEvent.next(nevent);
		}
		if (
			(event.status === 'new' || event.status === 'paid') &&
			event.isOnlinePayment
		) {
			this.newOnlineEvent.next(nevent);
		}

		if (
			[
				IProfessionalBankValidationStatus.has_errors,
				IProfessionalBankValidationStatus.on_going,
				IProfessionalBankValidationStatus.pending,
				IProfessionalBankValidationStatus.validated,
			].includes(event.status as IProfessionalBankValidationStatus)
		) {
			this.cacheService.update('wePayOutResponse', event);
			this.wePayOutEvent.next(nevent);
		}

		// check if we need to update the current week cache
		const cacheWeek = localStorage.getItem('dbEventsWeek');
		if (cacheWeek) {
			if (Number(cacheWeek) === moment.utc(event.day).isoWeek()) {
				await this.updateLocalEvent(nevent);
			}
			this.updatedEvent.next(nevent);
		}
	}

	// Aux for new events emitter.
	public eventEmitter(): Observable<ICalendarEvent> {
		// return this.foundNewEvents;
		return this.updatedEvent.asObservable();
	}

	// Aux for new online sessions emitter.
	public getNewOnlineSession(): Observable<ICalendarEvent> {
		return this.newOnlineEvent.asObservable();
	}

	public getCNPJValidation(): Observable<ICalendarEvent> {
		return this.idWallEvent.asObservable();
	}

	// Get user's events.
	public getEvents(minDate: Date, maxDate: Date): Observable<ICalendarEvent[]> {
		minDate.setHours(0, 0, 0, 0);
		maxDate.setHours(23, 59, 59);

		return this.getEventsFromServer({
			minDate: moment(minDate),
			maxDate: moment(maxDate),
		}).pipe(map((res) => res.items.map((e) => this.formatEvent(e))));
	}

	private getWeekDates(date: Date, firstWeekDay = 0) {
		const curWeekDay: number = date.getDay();
		const dateDif: number =
			curWeekDay > firstWeekDay ? curWeekDay - firstWeekDay : 0;
		const firstDate: Date = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate() - dateDif
		);
		const lastDate: Date = new Date(
			firstDate.getFullYear(),
			firstDate.getMonth(),
			firstDate.getDate() + 6
		);
		return { firstDate, lastDate };
	}

	public getEventsWithoutTags(lastDate: Date): Observable<ICalendarEvent[]> {
		const firstDate: Date = new Date(
			lastDate.getFullYear(),
			lastDate.getMonth(),
			lastDate.getDate() - 6
		);

		return this.getEventsFromServer({
			minDate: moment(firstDate),
			maxDate: moment(lastDate),
			onlyMissingTags: true,
			status: ['paid'],
		}).pipe(map((res) => res.items.map((e) => this.formatEvent(e))));
	}

	public getEventsWithoutPromptuary(
		lastDate: Date
	): Observable<ICalendarEvent[]> {
		const firstDate: Date = new Date(
			lastDate.getFullYear(),
			lastDate.getMonth(),
			lastDate.getDate() - 6
		);

		return this.getEventsFromServer({
			minDate: moment(firstDate),
			maxDate: moment(lastDate),
			status: ['paid'],
		}).pipe(map((res) => res.items.map((e) => this.formatEvent(e))));
	}

	// Get an event.
	public async getEvent(id: string, useCache = true): Promise<ICalendarEvent> {
		return new Promise(async (resolve, reject) => {
			if (useCache) {
				const result = await this.cacheService.getByKey(
					this.collectionName,
					id
				);
				if (
					!result.id ||
					(result.isB2C === undefined && result.status === 'paid')
				) {
					this.getEventInServer(id).subscribe(
						(event) => {
							const ev = this.formatEvent(event);
							this.cacheService.update(this.collectionName, ev);
							resolve(ev);
						},
						(error) => {
							reject(error);
						}
					);
				} else {
					resolve(result);
				}
			} else {
				this.getEventInServer(id).subscribe(
					(event) => {
						const ev = this.formatEvent(event);
						this.cacheService.update(this.collectionName, ev);
						resolve(ev);
					},
					(error) => {
						reject(error);
					}
				);
			}
		});
	}

	// Get an event from server
	public getEventInServer(id: string): Observable<IEvent> {
		return this.http.get<IEvent>(`appointments/${id}`);
	}

	// Get events for today.
	public getEventsForToday(): Observable<ICalendarEvent[]> {
		return this.getEventsForDay(new Date());
	}

	// Get events for a specific day.
	public getEventsForDay(today: Date): Observable<ICalendarEvent[]> {
		const maxDate: Date = new Date(today);
		today.setHours(0, 0, 0, 0);
		maxDate.setHours(23, 59, 59);

		return this.getEventsFromServer({
			minDate: moment(today),
			maxDate: moment(maxDate),
		}).pipe(map((res) => res.items.map((e) => this.formatEvent(e))));
	}

	// Filter events by name.
	public filterEvents(
		filter: any[],
		search?: SearchEvents
	): Promise<ICalendarEvent[]> {
		const indexDetails: any = {
			indexName: 'date',
			order: 'desc',
		};

		let events: ICalendarEvent[] = [];
		return new Promise((resolve, reject) => {
			if (filter && filter.length > 0) {
				this.cacheService.getAll(this.collectionName, null, indexDetails).then(
					(evts) => {
						evts.forEach((event: ICalendarEvent) => {
							const profileId: string = event.profileId;
							filter[0].forEach((f: any) => {
								if (f.profileId === profileId) {
									const formatedName: string = f.displayName || event.userName;
									event.userName = f.name || formatedName;
									events.push(event);
								}
							});
						});
						// lets search in the server in case we have no results
						if (events.length === 0 && search) {
							this.getEventsFromServer(search).subscribe((result) => {
								if (result.total > 0) {
									events = result.items
										.filter((e) => {
											filter[0].find((f: any) => f.profileId === e.profileId);
										})
										.map((e) => this.formatEvent(e));
								}
								return resolve(events);
							});
						}
						resolve(events);
					},
					(error) => {
						reject(error);
					}
				);
			} else {
				resolve(events);
			}
		});
	}

	// Get filtered clients (events search).
	public filterClientsOnEvents(
		filter: string,
		search?: SearchEvents
	): Promise<IClientEvent[]> {
		const indexDetails: any = {
			indexName: 'date',
		};
		const filterValue: string = filter.toLowerCase();
		let clients: IClientEvent[] = [];

		return new Promise<IClientEvent[]>((resolve) => {
			this.cacheService.openCursor(
				this.collectionName,
				(evt) => {
					const cursor = (evt.target as any).result;
					if (cursor) {
						// Get clients's name.
						const uName: string = cursor.value.userName;
						const isGenericUser = cursor.value.profileId === dummyUserId;
						// Add client if his name matches with filter value.
						if (
							uName &&
							uName.toLowerCase().indexOf(filterValue) > -1 &&
							!isGenericUser
						) {
							clients.push({
								id: cursor.value.id,
								profileId: cursor.value.profileId,
								contactId: cursor.value.contactId,
								name: uName,
							});
						}
						cursor.continue();
					} else {
						// lets search in the server in case we have no results
						if (clients.length === 0 && search) {
							this.getEventsFromServer(search).subscribe((result) => {
								if (result.total > 0) {
									clients = result.items
										.filter((e) => {
											return (
												e.userName &&
												e.userName.toLowerCase().indexOf(filterValue) > -1
											);
										})
										.map((e) => {
											return {
												id: e.id,
												profileId: e.profileId,
												contactId: e.contactId,
												name: e.userName,
											} as IClientEvent;
										});
								}
								return resolve(clients);
							});
						} else {
							resolve(clients);
						}
					}
				},
				null,
				indexDetails
			);
		});
	}

	// Get event status text.
	public getEventStatus(ev: ICalendarEvent): string {
		let statusText = '';
		switch (ev.status.toLowerCase()) {
			case 'invite':
				statusText = this.strStatusInvite;
				break;
			case 'new':
				statusText = this.strStatusNew;
				break;
			case 'paid':
				statusText = this.strStatusPaid;
				break;
			case 'reschedule':
				statusText = this.strStatusReschedule;
				break;
			case 'reschedule_request':
				statusText = ev.userRequestedReschedule
					? this.strStatusRescheduled
					: this.strStatusRescheduleRequest;
				break;
			case 'cancelled':
				statusText = this.strStatusCancelled;
				break;
			case 'reserved':
				statusText = this.strStatusWaitingPayment;
				break;
		}
		return statusText;
	}

	// Get bootstrap label class according to event status.
	public getLabelClass(status: string): string {
		let label: string;

		switch (status.toLowerCase()) {
			case 'invite':
			case 'reserved':
				label = 'label-info';
				break;
			case 'new':
				label = 'label-warning';
				break;
			case 'paid':
				label = 'label-success';
				break;
			case 'reschedule':
				label = 'label-default';
				break;
			case 'reschedule_request':
				label = 'label-primary';
				break;
			case 'cancelled':
				label = 'label-danger';
				break;
		}
		return label;
	}

	// Return if event can be reschedule.
	allowReschedule(event: IEvent): boolean {
		const nowDate = moment().seconds(0).milliseconds(0);
		const checkDate = moment(event.day);
		// Verify if is an paid event.
		return event.status === 'paid' && !nowDate.isAfter(checkDate);
	}

	// Return if the date is in the past
	isDateInThePast(date: Date): boolean {
		const eventDate = moment(date);
		const now = moment();
		return eventDate.isBefore(now);
	}

	// Save new event.
	saveNewEvent(event: INewEvent): Observable<IEvent> {
		let body: any;
		let url: string;
		// Is client or non Zenklub user?
		if (event.contactId && !event.userId) {
			body = {
				inviteExpiresMinutesBeforeSessions:
					event.inviteExpiresMinutesBeforeSessions,
				from: event.startDate,
				status: 'invite',
				price: event.price,
				contactReason: event.contactReason,
				professionalAddressId: event.professionalAddressId ?? null,
			};
			url = `appointments/contact/${event.contactId}`;
		} else {
			body = event;
			url = 'appointments';
		}

		if (event.repositionOriginalEventId) {
			body.repositionOriginalEventId = event.repositionOriginalEventId;
		}

		this.actionTracker.eventTracker(
			SCOPE_TYPE.Office,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.Client,
			INTERACTION_TYPE.Schedule,
			{
				clientId: event.userId,
				amountOfSessions: 1,
			}
		);

		// Try to post.
		return this.http.post<IEvent>(url, body);
	}

	scheduleRecurrencyAppointment(
		event: IRecurrencyAppointment
	): Observable<IRecurrencyScheduleResponse> {
		const baseUrl = environment.servicesUrl;
		const timezone = moment.tz.guess();
		if (event.recurrencyType === 'custom') {
			return this.http.post<IRecurrencyScheduleResponse>(
				`appointments/createMultiple`,
				{
					...event,
					timezone,
				}
			);
		}

		return this.http.post<IRecurrencyScheduleResponse>(
			`${baseUrl}/professional/appointments/recurring-invite`,
			{
				...event,
				timezone,
			}
		);
	}

	// Validate if an event can be started.
	public validateEventStart(
		eventId: string
	): Observable<IValidateStartResponse> {
		return this.http.patch<IValidateStartResponse>(
			`appointments/${eventId}/validateStart`,
			null
		);
	}

	// Update an event (reschedule).
	public updateEvent(
		eventId: string,
		event: IUpdateEvent
	): Observable<undefined> {
		return this.http.post<undefined>(
			`appointments/${eventId}/createReschedule`,
			event
		);
	}

	// Cancel an invite.
	public cancelInvite(eventId: string): Observable<undefined> {
		return this.http.put<undefined>(
			`appointments/${eventId}/cancelInvitation`,
			null
		);
	}

	// Cancel a reschedule.
	public cancelReschedule(eventId: string): Observable<undefined> {
		return this.http.put<undefined>(
			`appointments/${eventId}/cancelReschedule`,
			null
		);
	}

	// Confirms that an Event was successful
	public confirmEventFinish(
		eventId: string,
		event: IConfirmEvent
	): Observable<undefined> {
		return this.http.post<undefined>(
			`appointments/${eventId}/confirmEventFinish`,
			event
		);
	}

	// Confirms that an Event was successful
	public sendReceipt(eventId: string): Observable<undefined> {
		return this.http.post<undefined>(
			`appointments/${eventId}/generateReceipt`,
			null
		);
	}

	// Is event editable?
	public isEditable(event: IEvent): boolean {
		return !['reschedule', 'reschedule_request', 'invite'].includes(
			event.status
		);
	}

	public formatEvent(event: IEvent): ICalendarEvent {
		const nevent = { ...event } as ICalendarEvent;
		this.contacts.getContactImage(nevent).then((image) => {
			nevent.userImageUrl = image;
		});

		nevent.day = new Date(event.day);
		nevent.dayEnd = new Date(event.dayEnd);
		// setup key fields
		nevent.timestamp = new Date(event.day).getTime();
		nevent.cattimestamp = new Date(event.createdAt).getTime();
		nevent.uattimestamp = new Date(event.updatedAt).getTime();
		// Define start and end hours.
		nevent.startTime = this.dateTime.getFormatedHours(new Date(event.day));
		nevent.endTime = this.dateTime.getFormatedHours(new Date(event.dayEnd));

		nevent.userRequestedReschedule =
			this.checkUserHasRequestedReschedule(nevent);

		nevent.statusText = this.getEventStatus(nevent);
		if (event.status === 'new' && !event.isOnlinePayment) {
			nevent.status = 'reserved';
		}

		return nevent;
	}

	private checkUserHasRequestedReschedule(event: IEvent) {
		if (event.status === 'reschedule_request') {
			return false;
		}

		if (event.reschedule) {
			const professionalId = this.firebaseUser?.uid;
			return event.reschedule.rescheduledBy !== professionalId;
		}

		return undefined;
	}

	public getEventPriceToShow(event: IEvent, short = false): EventPrice {
		const key: string = short ? 'GENERAL.PROFITSHORT' : 'GENERAL.PROFIT';
		const toRet: EventPrice = {
			title: '',
			extraInfo: [],
			value: '',
		};

		const hasFinal =
			event.compensationFinal && event.compensationFinal.compensationInfoText;

		if (!short && event.compensationInfoText && !hasFinal) {
			const info = this.reduceObject(event.compensationInfoText);
			for (const k of Object.keys(info)) {
				toRet.extraInfo.push(info[k]);
			}
		} else if (!short && hasFinal) {
			const info = this.reduceObject(
				event.compensationFinal.compensationInfoText
			);
			for (const k of Object.keys(info)) {
				toRet.extraInfo.push(info[k]);
			}
		}

		// Professional profit.
		const doctValue = (() => {
			const finalCompensation = event.compensationFinal;
			if (finalCompensation && finalCompensation.compensation) {
				return finalCompensation.compensation;
			}

			return event.professionalCompensation
				? event.professionalCompensation
				: 0;
		})();

		const stringValue = this.currency.getValueForScreen(
			doctValue,
			event?.currency || 'BRL'
		);

		toRet.title = this.translate.instant(key, { value: stringValue });
		toRet.value = stringValue;
		toRet.valueNumber = doctValue;
		return toRet;
	}

	public getSubscriptionDescription(event: IEvent): string | null {
		if (event.subscription && event.subscription?.description) {
			return event.subscription?.description;
		}
		return null;
	}

	private reduceObject(context: any, nKey?: string) {
		const contextK = Object.keys(context);
		let nContext: any = {};
		for (const k of contextK) {
			if (
				context[k] &&
				typeof context[k] === 'object' &&
				context[k].constructor === Object &&
				!Array.isArray(context[k])
			) {
				const kk = nKey ? `${nKey}.${k}` : k;
				const aux = this.reduceObject(context[k], kk);
				nContext = {
					...nContext,
					...aux,
				};
			} else if (nKey) {
				nContext[`${nKey}.${k}`] = context[k];
			} else {
				nContext[k] = context[k];
			}
		}
		return nContext;
	}

	getRecurrenciesAttempts(
		params: IRecurrenciesAttemptsParams
	): Observable<IRecurrenciesAttemptsResponse> {
		const baseUrl = environment.servicesUrl;
		return this.http.get<IRecurrenciesAttemptsResponse>(
			`${baseUrl}/professional/recurrent-schedules/attempts?startDate=${params.startDate}&endDate${params.endDate}`
		);
	}

	cancelRecurrencyInvites(sessionId: string, removeRange): Observable<any> {
		const baseUrl = environment.servicesUrl;
		return this.http.delete<any>(
			`${baseUrl}/professional/appointments/recurring-invite/session/${sessionId}?removeRange=${removeRange}`
		);
	}
}
